import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useFieldArray, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { AddActionDialogType, AddColumnDialogType, ColumnForm } from '@pages/EntityCreateOrEdit'
import { getObjectXTotalRowDefaultValue } from '@pages/TimeLineCreateOrEdit/helpers'

import {
  useExecuteCommandMutation,
  useFetchDictionariesByCategoryCodeQuery,
  useFetchObjectByCodeNoCacheMutation,
} from '@redux/api'

import { BIND_TYPE } from '@constants'
import { CATEGORIES, TIMELINE_ACTION_COLUMNS } from '@constants'
import {
  ActionRow,
  AutocompleteOption,
  CommandParametersType,
  ConfiguredColumn,
  Dictionary,
  GETShareColumn,
  ModalType,
  ObjectDataRecord,
  TimeLineGridConfig,
} from '@types'

type UseObjectXHandlersType = {
  methods: UseFormReturn<TimeLineGridConfig>
  bindingValuesColumns?: Dictionary[]
  handleSaveTimeLine: () => void
}

export const useObjectXHandlers = (props: UseObjectXHandlersType) => {
  const {
    methods: { setValue, watch, control },
    bindingValuesColumns,
    handleSaveTimeLine,
  } = props

  const { t } = useTranslation()

  const watchObjectXObject = watch('objectX.object')
  const watchObjectXCommandCode = watch('objectX.getDataCommand.name')
  const watchObjectXCommand = watch('objectX.getDataCommand')
  const watchObjectXCommandParameters = watch('objectX.getDataCommand.parameters')
  const watchObjectXCommandBody = watch('objectX.getDataCommand.body')
  const watchObjectXColumns = watch('objectX.columns')
  const watchObjectXCellValue = watch('objectX.cell.value')
  const watchObjectXCellBindType = watch('objectX.cell.bindType')
  const watchObjectXActions = watch('objectX.actions')
  const watchObjectXTotalsRows = watch('objectX.totalsRows')
  const watchObjectXEntityId = watch('objectX.entityId')
  const watchObjectXColumnsObject = watch('objectX.columnsObject.object')
  const watchObjectXColumnsObjectCommandCode = watch('objectX.columnsObject.command.name')
  const watchObjectXColumnsObjectCommand = watch('objectX.columnsObject.command')
  const watchObjectXColumnsObjectHeaderBindType = watch('objectX.columnsObject.headerBindType')

  const { append, remove } = useFieldArray({
    name: 'objectX.totalsRows',
    control,
  })

  const [fetchObjectByCode, { data }] = useFetchObjectByCodeNoCacheMutation()
  const [fetchColumnsObjectByCode, { data: columnsObjectData }] =
    useFetchObjectByCodeNoCacheMutation()
  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TIME_TABLE_OBJECT_X_ACTION
  )
  const [executeCommand] = useExecuteCommandMutation()

  const [objectXCurrentTab, setObjectXCurrentTab] = useState(0)
  const [objectXShowCellScriptValueDialog, setObjectXShowCellScriptValueDialog] = useState(false)
  const [showAddColumnDialog, setShowAddColumnDialog] = useState(false)
  const [addColumnDialogData, setAddColumnDialogData] = useState<AddColumnDialogType | undefined>(
    undefined
  )
  const [showAddActionDialog, setShowAddActionDialog] = useState(false)
  const [addActionDialogData, setAddActionDialogData] =
    useState<
      Omit<AddActionDialogType, 'createAction' | 'editAction' | 'showDialog' | 'onCloseModal'>
    >()
  const [objectXCellPreviewData, setObjectXCellPreviewData] = useState<ObjectDataRecord[]>({})

  const selectedObjectCommandOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectXObject || !data?.commands?.length) {
      return []
    }

    return (
      data.commands.map(field => ({
        id: field.code,
        label: field.code,
        httpMethod: field.httpMethod,
      })) || []
    )
  }, [data])

  const selectedObjectFields = useMemo(() => {
    if (!watchObjectXObject || !data?.fields?.length) {
      return []
    }

    return data.fields
  }, [data])

  const selectedColumnsObjectFields = useMemo(() => {
    if (!columnsObjectData?.fields?.length) {
      return []
    }

    return columnsObjectData.fields
  }, [columnsObjectData])

  const selectedObjectFieldsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectXObject || !data?.fields?.length) {
      return []
    }

    return data.fields.map(field => ({ id: field.name, label: field.name })) || []
  }, [watchObjectXObject, data])

  const columnsObjectCommandsOptions: AutocompleteOption[] = useMemo(() => {
    if (!watchObjectXColumnsObject || !columnsObjectData?.commands?.length) {
      return []
    }

    return (
      columnsObjectData.commands.map(field => ({
        id: field.code,
        label: field.code,
        httpMethod: field.httpMethod,
      })) || []
    )
  }, [watchObjectXColumnsObject, columnsObjectData])

  const objectXBindingValuesColumns = useMemo(() => {
    if (!bindingValuesColumns?.length) {
      return []
    }

    return bindingValuesColumns.filter(
      item => item.code !== BIND_TYPE.FIELD && item.code !== BIND_TYPE.ACTION
    )
  }, [bindingValuesColumns])

  useEffect(() => {
    if (watchObjectXObject?.id) {
      fetchObjectByCode(watchObjectXObject.id as string)
    }
  }, [watchObjectXObject?.id])

  useEffect(() => {
    if (watchObjectXColumnsObject?.id) {
      fetchColumnsObjectByCode(watchObjectXColumnsObject.id as string)
    }
  }, [watchObjectXColumnsObject?.id])

  useEffect(() => {
    if (watchObjectXObject?.id && watchObjectXCommand?.name?.id) {
      executeCommand({
        objectCode: watchObjectXObject.id as string,
        commandCode: watchObjectXCommand?.name?.id as string,
        method: watchObjectXCommand?.name?.httpMethod,
        page: 0,
        size: 1,
      })
        .unwrap()
        .then(setObjectXCellPreviewData)
    }
  }, [watchObjectXObject, watchObjectXCommand])

  const handleChangeObjectXObject = (_: AutocompleteOption) => {
    setValue('objectX.getDataCommand.name', null)
    setValue('objectX.startDateField', null)
    setValue('objectX.endDateField', null)
    setValue('objectX.linkToObjectY.keyField', '')
    setValue('objectX.linkToObjectY.pathArray', [])
    setValue('objectX.linkToColumnsObject.keyField', '')
    setValue('objectX.linkToColumnsObject.pathArray', [])
    setValue('objectX.fieldFromColumnsObjectToLink.keyField', '')
    setValue('objectX.fieldFromColumnsObjectToLink.pathArray', [])
    setValue('objectX.fieldFromObjectYToLink.keyField', '')
    setValue('objectX.fieldFromObjectYToLink.pathArray', [])
    setValue('objectX.keyField.keyField', null)
    setValue('objectX.keyField.pathArray', [])
    setValue('objectX.getDataCommand.parameters', [])
    setValue('objectX.getDataCommand.body', [])
  }

  const handleChangeObjectXCommand = (_: AutocompleteOption) => {
    setValue('objectX.getDataCommand.body', [] as CommandParametersType[])
    setValue('objectX.getDataCommand.parameters', [] as CommandParametersType[])
  }

  const handleChangeObjectXColumnsObject = (_: AutocompleteOption) => {
    setValue('objectX.columnsObject.command.name', null)
    setValue('objectX.columnsObject.command.parameters', [])
    setValue('objectX.columnsObject.command.body', [])
  }

  const handleChangeObjectXColumnsObjectCommand = (_: AutocompleteOption) => {
    setValue('objectX.columnsObject.command.body', [] as CommandParametersType[])
    setValue('objectX.columnsObject.command.parameters', [] as CommandParametersType[])
  }

  const handleChangeObjectXColumnsObjectHeaderBindType = (option: AutocompleteOption | null) => {
    if (option?.id === BIND_TYPE.FIELD) {
      setValue('objectX.columnsObject.headerScript', '')
    } else if (option?.id === BIND_TYPE.JS || option?.id === BIND_TYPE.JSX) {
      setValue('objectX.columnsObject.headerField', { keyField: null, pathArray: [] })
    }
  }

  const handleChangeObjectXCommandParameters = (fields: CommandParametersType[]) => {
    setValue('objectX.getDataCommand.parameters', fields, {
      shouldDirty: true,
    })
  }

  const handleChangeObjectXCommandBody = (fields: CommandParametersType[]) => {
    setValue('objectX.getDataCommand.body', fields, { shouldDirty: true })
  }

  const handleChangeObjectXCurrentTab = (_: SyntheticEvent, value: number) => {
    setObjectXCurrentTab(value)
  }

  const handleChangeObjectXCellValue = (value: string) => {
    setValue('objectX.cell.value', value, { shouldDirty: true, shouldValidate: true })
  }

  const handleChangeObjectXShowCellScriptValueDialog = (value: boolean) => {
    setObjectXShowCellScriptValueDialog(value)
  }

  const handleChangeObjectXColumns = (data: ColumnForm) => {
    const columnIndex = watchObjectXColumns?.findIndex(item => item.id === data.id)

    if (columnIndex < 0) {
      setValue('objectX.columns', watchObjectXColumns.concat(data))

      return
    }

    watchObjectXColumns[columnIndex] = data // TODO проверить тип у watchObjectXColumns

    setValue('objectX.columns', watchObjectXColumns)
  }

  const handleDeleteObjectXColumn = (id: string | number) => {
    setValue(
      'objectX.columns',
      watchObjectXColumns.filter(item => item.id !== id)
    )

    handleSaveTimeLine()
  }

  const handleCloseObjectXAddColumnDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddColumnDialog(false)
      setAddColumnDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddColumnDialog(false)
      setAddColumnDialogData(undefined)
    }
  }

  const handleOpenObjectXAddColumnDialog = (type: ModalType, id?: string | number) => {
    const columnData = watchObjectXColumns?.find(item => item.id === id)

    setShowAddColumnDialog(true)
    setAddColumnDialogData({
      columns: watchObjectXColumns,
      currentRow: columnData as ConfiguredColumn,
      modalType: type,
      objectCode: watchObjectXObject?.id as string,
      bindingValuesColumns: objectXBindingValuesColumns,
      rawObjectFields: selectedObjectFields,
      actionTable: {
        rows: [],
        columns: [],
      },
    })
  }

  const handleCloseObjectXAddActionDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)
    }
  }

  const handleOpenObjectXAddActionDialog = (modalType: ModalType, id?: string | number) => {
    const actionData = watchObjectXActions?.find(item => item.id === id)

    setShowAddActionDialog(true)
    setAddActionDialogData({
      modalType,
      bindingValuesAction,
      objectCode: watchObjectXObject?.id as string,
      actionEventsCategoryCode: CATEGORIES.BINDING_TIME_TABLE_OBJECT_X_EVENTS_ACTION,
      currentRow: actionData as GETShareColumn,
      actionTable: {
        columns: TIMELINE_ACTION_COLUMNS,
        rows: watchObjectXActions,
      },
      columns: [],
    })
  }

  const handleChangeObjectXAction = (data: ActionRow) => {
    const columnIndex = watchObjectXActions?.findIndex(item => item.id === data.id)

    if (columnIndex && columnIndex < 0) {
      setValue('objectX.actions', watchObjectXActions.concat(data))

      return
    }

    watchObjectXActions[columnIndex] = data
    setValue('objectX.actions', watchObjectXActions)
  }

  const handleDeleteObjectXAction = (id: string | number) => {
    setValue(
      'objectX.actions',
      watchObjectXActions.filter(item => item.id !== id)
    )

    return handleSaveTimeLine()
  }

  const handleAddObjectXTotalRow = () => {
    const emptyValue = { ...getObjectXTotalRowDefaultValue(), id: uuid() }
    append(emptyValue)
  }

  const handleDeleteObjectXTotalRow = (index: number) => {
    remove(index)
  }

  return {
    state: {
      objectXCurrentTab,
      objectXShowCellScriptValueDialog,
      watchObjectXObject,
      watchObjectXCommandCode,
      watchObjectXCommandParameters,
      watchObjectXCommandBody,
      watchObjectXColumns,
      watchObjectXCellValue,
      watchObjectXCellBindType,
      watchObjectXTotalsRows,
      watchObjectXActions,
      watchObjectXEntityId,
      watchObjectXColumnsObject,
      watchObjectXColumnsObjectCommandCode,
      watchObjectXColumnsObjectCommand,
      watchObjectXColumnsObjectHeaderBindType,
      objectXShowAddColumnDialog: showAddColumnDialog,
      objectXAddColumnDialogData: addColumnDialogData,
      objectXShowAddActionDialog: showAddActionDialog,
      objectXAddActionDialogData: addActionDialogData,
      objectXCellPreviewData,
    },
    data: {
      objectXSelectedObjectCommandsOptions: selectedObjectCommandOptions,
      objectXSelectedObjectFieldsOptions: selectedObjectFieldsOptions,
      objectXSelectedObjectFields: selectedObjectFields,
      objectXColumnsObjectCommandsOptions: columnsObjectCommandsOptions,
      objectXSelectedColumnsObjectFields: selectedColumnsObjectFields,
    },
    handlers: {
      handleChangeObjectXObject,
      handleChangeObjectXCommand,
      handleChangeObjectXColumnsObject,
      handleChangeObjectXColumnsObjectCommand,
      handleChangeObjectXColumnsObjectHeaderBindType,
      handleChangeObjectXCommandParameters,
      handleChangeObjectXCommandBody,
      handleChangeObjectXCurrentTab,
      handleChangeObjectXShowCellScriptValueDialog,
      handleChangeObjectXCellValue,
      handleChangeObjectXColumns,
      handleCloseObjectXAddColumnDialog,
      handleOpenObjectXAddColumnDialog,
      handleDeleteObjectXColumn,
      handleOpenObjectXAddActionDialog,
      handleCloseObjectXAddActionDialog,
      handleChangeObjectXAction,
      handleDeleteObjectXAction,
      handleAddObjectXTotalRow,
      handleDeleteObjectXTotalRow,
    },
  }
}
